"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Trophy, Users, MapPin, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const timelineEvents = [{
  year: "1933",
  title: "The Foundation",
  description: "WT Birkalla is officially established, bringing together local talent and laying the groundwork for a rich footballing tradition in South Australia.",
  icon: Users,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1933.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1933.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1933.png-wvc-sizes",
    alt: "WT Birkalla founding members in 1933"
  }
}, {
  year: "1950",
  title: "First Major Triumph",
  description: "The club secures its first major championship, cementing its reputation as a formidable force in the state league and igniting the 'Yellow & Black' passion.",
  icon: Trophy,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1950.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1950.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1950.png-wvc-sizes",
    alt: "1950 Championship winning team"
  }
}, {
  year: "1978",
  title: "A Place to Call Home",
  description: "Relocation to the current Birkalla grounds. The new facilities provide a permanent home for both junior development and senior success.",
  icon: MapPin,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1978.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1978.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1978.png-wvc-sizes",
    alt: "Construction of the new Birkalla grounds"
  }
}, {
  year: "1998",
  title: "Expanding the Legacy",
  description: "The official introduction of the Women's and Girls' programs, marking a significant step towards an inclusive, community-wide football club.",
  icon: Star,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1998.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1998.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_1998.png-wvc-sizes",
    alt: "Inaugural women's team"
  }
}, {
  year: "Present Day",
  title: "The Modern Era",
  description: "Today, WT Birkalla stands as a pillar of the community, boasting state-of-the-art facilities, elite coaching, and comprehensive pathways for all ages.",
  icon: Trophy,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_present.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_present.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_history_present.png-wvc-sizes",
    alt: "Modern WT Birkalla senior team in action"
  }
}];
function TimelineItem({
  item,
  index,
  isLast
}: {
  item: (typeof timelineEvents)[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const Icon = item.icon;
  return <div ref={ref} data-index={index} className={`relative flex flex-col md:flex-row w-full group transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      {!isLast && <div className="absolute left-6 md:left-1/3 top-12 bottom-[-3rem] w-px bg-gradient-to-b from-primary/50 to-primary/10 md:-translate-x-1/2 z-0" />}

      {}
      <div className="absolute left-6 md:left-1/3 top-2 md:top-4 w-4 h-4 rounded-full bg-background border-2 border-primary md:-translate-x-1/2 z-10 group-hover:bg-primary group-hover:scale-150 transition-all duration-500 shadow-[0_0_15px_rgba(var(--color-primary),0.5)]" />

      {}
      <div className="w-full md:w-1/3 pl-16 md:pl-0 md:pr-16 pt-0 md:pt-2 text-left md:text-right z-10">
        <h3 className="text-5xl md:text-7xl lg:text-8xl font-black font-default tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-primary via-primary/80 to-primary/20 opacity-80 group-hover:opacity-100 transition-opacity duration-500" style={{
        WebkitTextStroke: "1px rgba(255,255,255,0.1)"
      }}>
          {item.year}
        </h3>
      </div>

      {}
      <div className="w-full md:w-2/3 pl-16 md:pl-16 pb-16 md:pb-24 pt-4 md:pt-0 z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-md bg-primary/10 text-primary">
            <Icon className="w-5 h-5" />
          </div>
          <h4 className="text-2xl md:text-3xl font-bold font-default text-foreground">
            {item.title}
          </h4>
        </div>
        
        <p className="text-muted-foreground text-lg mb-8 max-w-2xl leading-relaxed">
          {item.description}
        </p>

        {}
        <div className="relative rounded-xl overflow-hidden aspect-video md:aspect-[21/9] max-w-3xl shadow-2xl border border-border/50 group-hover:border-primary/30 transition-colors duration-500">
          <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" className="w-full h-full object-cover sepia-[0.6] contrast-125 brightness-90 group-hover:sepia-0 group-hover:contrast-100 group-hover:brightness-100 transition-all duration-700 scale-105 group-hover:scale-100" />
          {}
          <div className="absolute inset-0 bg-primary/20 mix-blend-color group-hover:opacity-0 transition-opacity duration-700 pointer-events-none" />
          {}
          <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIj48ZmlsdGVyIGlkPSJuIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iMC44IiBudW1PY3RhdmVzPSIzIiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI24pIi8+PC9zdmc+')]"></div>
        </div>
      </div>
    </div>;
}
export default function TheYellowBlackEra() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="the-yellow-black-era" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-primary/5 blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 md:mb-32 transition-all duration-1000 flex flex-col items-center ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Link to="/">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/wt_birkalla_logo_gold.png" alt="WT Birkalla Logo" loading="lazy" className="h-20 w-auto object-contain mb-6" />
          </Link>
          <h2 className="text-sm font-bold tracking-widest text-primary uppercase mb-4 font-default">
            Club History
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-black font-default mb-6 text-foreground">
            The Yellow & Black Era
          </h3>
          <p className="text-xl text-muted-foreground">
            A journey through time, celebrating the milestones, the people, and the passion that built WT Birkalla into the community powerhouse it is today.
          </p>
        </div>

        {}
        <div className="relative">
          {timelineEvents.map((item, index) => <TimelineItem key={index} item={item} index={index} isLast={index === timelineEvents.length - 1} />)}
        </div>

        {}
        <div className="mt-16 md:mt-24 text-center relative z-20">
          <Link to="/OurLegacyPage">
            <Button size="lg" className="group text-lg px-8 py-6 rounded-full shadow-xl shadow-primary/20 hover:shadow-primary/40 transition-all duration-300">
              Explore Our Full Legacy
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}